import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Badge, Btn, Card, MetricCard, SectionHeader, DataTable, FormRow, Field, Modal, FormActions, Avatar, SearchableSelect } from '../../components/ui';
import { Icon } from '../../icons';
import { ConfirmModal } from '../../components/shared';
import { saveAndOpenFile } from '../../utils/fileUtils';
import AmenityService from '../../api/amenity';
import CalendarView from './CalendarView';
import ReportsView from './ReportsView';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const STATUS_COLOR_MAP = { Available: "green", Booked: "red", Maintenance: "amber", Closed: "gray" };

export default function AmenitiesScreen() {
  const { user } = useAuth();
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.amenities?.[key] || translations.en?.amenities?.[key] || translations[language]?.[key] || translations.en?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [editAmenity, setEditAmenity] = useState(null);
  const [delBooking, setDelBooking] = useState(null);
  const [cancelBookingLoading, setCancelBookingLoading] = useState(false);
  const [delAmenity, setDelAmenity] = useState(null);
  const [delAmenityLoading, setDelAmenityLoading] = useState(false);
  const [viewAmenity, setViewAmenity] = useState(null);
  const [viewBooking, setViewBooking] = useState(null);
  const [markPaidBooking, setMarkPaidBooking] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const [activeTab, setActiveTab] = useState('table'); // table, calendar, reports
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAmenity, setFilterAmenity] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [filterBooking, setFilterBooking] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const resetAllFilters = () => {
    setSearchQuery("");
    setFilterAmenity("");
    setFilterPayment("");
    setFilterBooking("");
    setSortField("date");
    setSortOrder("desc");
    setPage(1);
  };

  const activeFiltersCount = (filterAmenity ? 1 : 0) + (filterPayment ? 1 : 0) + (filterBooking ? 1 : 0);
  const hasActiveFilters = activeFiltersCount > 0 || !!searchQuery;

  const [amenities, setAmenities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [units, setUnits] = useState([]);
  const [metrics, setMetrics] = useState({
    total_amenities: 0,
    bookings_this_month: 0,
    revenue_this_month: 0,
    pending_payments: 0
  });

  const filteredBookings = useMemo(() => {
    return (bookings || []).filter(b => {
      if (activeFilter === 'this_month') {
        const bDate = new Date(b.date);
        const now = new Date();
        if (!(bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear())) return false;
      }
      if (activeFilter === 'revenue') {
        const bDate = new Date(b.date);
        const now = new Date();
        if (!(bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear() && b.payment_status === 'paid')) return false;
      }
      if (activeFilter === 'pending' && b.payment_status !== 'pending') return false;

      if (filterAmenity && String(b.amenity_id) !== String(filterAmenity)) return false;
      if (filterPayment && b.payment_status !== filterPayment) return false;
      if (filterBooking && b.booking_status !== filterBooking) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!b.booked_by?.toLowerCase().includes(q) && !b.unit?.toLowerCase().includes(q) && !b.booking_id?.toLowerCase().includes(q) && !b.transaction_id?.toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === 'date') {
        valA = new Date(a.created_at || `${a.date}T${a.start_time || '00:00:00'}`).getTime() || 0;
        valB = new Date(b.created_at || `${b.date}T${b.start_time || '00:00:00'}`).getTime() || 0;
        if (valA === valB) {
          valA = a.id || 0;
          valB = b.id || 0;
        }
      } else if (sortField === 'amount') {
        valA = parseFloat(a.amount || 0);
        valB = parseFloat(b.amount || 0);
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [bookings, activeFilter, filterAmenity, filterPayment, filterBooking, searchQuery, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const paginatedBookings = filteredBookings.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const defB = { amenity_id: "", booked_by: "", unit: "", date: new Date().toISOString().split('T')[0], start_time: "18:00", end_time: "19:00", number_of_people: "", amount: "", payment_status: "pending" };
  const defA = { icon: "", name: "", capacity: "", price: "", price_type: "per_slot", opening_time: "06:00", closing_time: "22:00", slot_duration_minutes: 60, status: "Available", is_active: true };
  const [bForm, setBForm] = useState(defB);
  const [aForm, setAForm] = useState(defA);
  const [errors, setErrors] = useState({});
  const [isAmountEdited, setIsAmountEdited] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState("");

  const availableBlocks = useMemo(() => {
    const blocks = Array.from(new Set((units || []).map(u => u.block).filter(Boolean)));
    return blocks.sort();
  }, [units]);

  const filteredUnitsForBlock = useMemo(() => {
    if (!selectedBlock) {
      return units || [];
    }
    return (units || []).filter(u => String(u.block) === String(selectedBlock));
  }, [units, selectedBlock]);

  const bf = k => e => {
    setBForm(f => ({ ...f, [k]: e.target.value }));
    if (k === 'amount') setIsAmountEdited(true);
    if (k === 'amenity_id') setIsAmountEdited(false);
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  const af = k => e => {
    setAForm(f => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  useEffect(() => {
    if (modal !== "booking" || isAmountEdited) return;

    const selected = amenities?.find(a => String(a.id) === String(bForm.amenity_id));
    if (selected && selected.price) {
      const price = parseFloat(selected.price);
      const numPeople = parseInt(bForm.number_of_people) || 1;
      let amt = 0;

      if (selected.price_type === 'per_person') {
        amt = price * numPeople;
      } else if (selected.price_type === 'per_booking') {
        amt = price;
      } else {
        if (bForm.start_time && bForm.end_time && selected.slot_duration_minutes) {
          const [sh, sm] = bForm.start_time.split(':').map(Number);
          const [eh, em] = bForm.end_time.split(':').map(Number);
          const diffMins = (eh * 60 + em) - (sh * 60 + sm);
          if (diffMins > 0) {
            const slots = Math.ceil(diffMins / selected.slot_duration_minutes);
            if (selected.price_type === 'per_person_per_slot') {
              amt = slots * price * numPeople;
            } else {
              amt = slots * price;
            }
          }
        }
      }
      if (amt >= 0 && String(amt) !== bForm.amount) {
        setBForm(f => ({ ...f, amount: String(amt) }));
      }
    }
  }, [bForm.amenity_id, bForm.start_time, bForm.end_time, bForm.number_of_people, amenities, modal, isAmountEdited]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [amenRes, bookRes, metricRes, unitRes] = await Promise.all([
        AmenityService.getAmenities(),
        AmenityService.getBookings(),
        AmenityService.getMetrics(),
        AmenityService.getUnits()
      ]);
      setAmenities(amenRes?.data || (Array.isArray(amenRes) ? amenRes : []));
      setBookings(bookRes?.data || (Array.isArray(bookRes) ? bookRes : []));
      setMetrics(metricRes?.data || metricRes || { total_amenities: 0, bookings_this_month: 0, revenue_this_month: 0, pending_payments: 0 });
      const unitData = Array.isArray(unitRes?.data)
        ? unitRes.data
        : (Array.isArray(unitRes)
            ? unitRes
            : (Array.isArray(unitRes?.data?.data) ? unitRes.data.data : []));
      setUnits(unitData);
    } catch (err) {
      toast.error(t("toast_load_failed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetchData();
  }, [fetchData]);

  const validateAmenity = () => {
    const newErrors = {};
    if (!aForm.name) newErrors.name = t("val_name");
    if (!aForm.capacity) newErrors.capacity = t("val_capacity");
    if (!aForm.status) newErrors.status = t("val_status");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBooking = () => {
    const newErrors = {};
    if (!bForm.amenity_id) newErrors.amenity_id = t("val_amenity");
    if (!bForm.booked_by) newErrors.booked_by = t("val_name");
    if (!isMember && availableBlocks.length > 0 && !selectedBlock) newErrors.block = t("val_block") || "Block is required";
    if (!bForm.unit) newErrors.unit = t("val_unit");
    if (!bForm.date) newErrors.date = t("val_date");
    if (bForm.amount === "" || bForm.amount === null || bForm.amount === undefined) newErrors.amount = t("val_amount") || "Amount is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveBooking = async () => {
    if (!validateBooking()) return;

    setLoading(true);
    try {
      const payload = {
        ...bForm,
        ...(isMember ? { payment_status: 'pending' } : {})
      };
      if (editBooking) {
        await AmenityService.updateBooking(editBooking.id, payload);
        toast.success(t("toast_booking_updated"));
      } else {
        await AmenityService.createBooking(payload);
        toast.success(t("toast_booking_confirmed"));
      }
      setModal(null);
      setEditBooking(null);
      setBForm(defB);
      fetchData();
    } catch (err) {
      toast.error(err.message || t("toast_save_booking_failed"));
    } finally {
      setLoading(false);
    }
  };

  const saveAmenity = async () => {
    if (!validateAmenity()) return;

    setLoading(true);
    try {
      if (editAmenity) {
        await AmenityService.updateAmenity(editAmenity.id, aForm);
        toast.success(t("toast_amenity_updated"));
      } else {
        await AmenityService.createAmenity(aForm);
        toast.success(t("toast_amenity_added"));
      }
      setModal(null);
      setEditAmenity(null);
      setAForm(defA);
      fetchData();
    } catch (err) {
      toast.error(err.message || t("toast_save_amenity_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBooking = async (b) => {
    try {
      await AmenityService.approveBooking(b.id);
      toast.success(t("toast_booking_approved"));
      fetchData();
    } catch (err) {
      toast.error(err.message || t("toast_approve_booking_failed"));
    }
  };

  const handleRejectBooking = async (b) => {
    try {
      await AmenityService.rejectBooking(b.id);
      toast.success(t("toast_booking_rejected"));
      fetchData();
    } catch (err) {
      toast.error(err.message || t("toast_reject_booking_failed"));
    }
  };

  const openEditAmenity = a => {
    setEditAmenity(a);
    setAForm({
      icon: a.icon || "",
      name: a.name,
      capacity: String(a.capacity),
      price: a.price || "",
      price_type: a.price_type || "per_slot",
      opening_time: a.opening_time ? a.opening_time.substring(0, 5) : "06:00",
      closing_time: a.closing_time ? a.closing_time.substring(0, 5) : "22:00",
      slot_duration_minutes: a.slot_duration_minutes || 60,
      status: a.status,
      is_active: a.is_active ?? true
    });
    setErrors({});
    setModal("amenity");
  };

  const handleToggleActive = async (a) => {
    setLoading(true);
    try {
      await AmenityService.updateAmenity(a.id, { is_active: !a.is_active });
      toast.success(!a.is_active ? "Amenity enabled" : "Amenity disabled");
      fetchData();
    } catch (err) {
      toast.error(err.message || t("toast_save_amenity_failed"));
    } finally {
      setLoading(false);
    }
  };

  const confirmDelBooking = async () => {
    if (!delBooking || cancelBookingLoading) return;
    try {
      setCancelBookingLoading(true);
      await AmenityService.rejectBooking(delBooking.id);
      toast.success(t("toast_booking_cancelled"), { id: 'booking-cancel' });
      setDelBooking(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t("toast_delete_booking_failed"), { id: 'booking-cancel' });
    } finally {
      setCancelBookingLoading(false);
    }
  };

  const confirmMarkPaid = async () => {
    try {
      setMarkPaidLoading(true);
      await AmenityService.updateBooking(markPaidBooking.id, { payment_status: 'paid', transaction_id: bForm.transaction_id || null });
      toast.success(t("toast_booking_updated"));
      setMarkPaidBooking(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t("toast_save_booking_failed"));
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleDownloadReceipt = async (booking) => {
    const toastId = toast.loading("Downloading receipt...");
    try {
      const res = await AmenityService.downloadReceipt(booking.id);
      const blob = res instanceof Blob ? res : new Blob([res], { type: 'application/pdf' });
      const filename = `receipt_${booking.booking_id || booking.id}.pdf`;
      await saveAndOpenFile(blob, filename);
      toast.success("Receipt downloaded successfully", { id: toastId });
    } catch (err) {
      let errMsg = err.message || "Failed to download receipt";
      if (err instanceof Blob || err.response?.data instanceof Blob) {
        try {
          const text = await (err.text ? err.text() : err.response.data.text());
          const json = JSON.parse(text);
          if (json.message) errMsg = json.message;
        } catch (_) {}
      }
      toast.error(errMsg, { id: toastId });
    }
  };

  const confirmDelAmenity = async () => {
    if (!delAmenity || delAmenityLoading) return;
    try {
      setDelAmenityLoading(true);
      await AmenityService.deleteAmenity(delAmenity.id);
      toast.success(t("toast_amenity_deleted"), { id: 'amenity-delete' });
      setDelAmenity(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t("toast_delete_amenity_failed"), { id: 'amenity-delete' });
    } finally {
      setDelAmenityLoading(false);
    }
  };

  const openEditBooking = b => {
    setEditBooking(b);
    const matched = (units || []).find(u => String(u.unit_number) === String(b.unit));
    setSelectedBlock(matched?.block || "");
    setBForm({
      amenity_id: b.amenity_id,
      booked_by: b.booked_by,
      unit: b.unit || (isMember && units?.length === 1 ? units[0].unit_number : ""),
      date: b.date,
      start_time: b.start_time ? b.start_time.substring(0, 5) : "18:00",
      end_time: b.end_time ? b.end_time.substring(0, 5) : "19:00",
      number_of_people: String(b.number_of_people || ""),
      amount: String(b.amount),
      payment_status: b.payment_status,
      booking_status: b.booking_status
    });
    setErrors({});
    setIsAmountEdited(false);
    setModal("booking");
  };

  const openNewBooking = () => {
    setEditBooking(null);
    const defaultUnit = (isMember && user?.unit?.unit_number) || (isMember && units?.length === 1 ? units[0].unit_number : "");
    const matchedUnit = (units || []).find(u => String(u.unit_number) === String(defaultUnit));
    setSelectedBlock(matchedUnit?.block || "");
    setBForm({ ...defB, unit: defaultUnit, payment_status: "pending" });
    setErrors({});
    setIsAmountEdited(false);
    setModal("booking");
  };

  const openMarkPaid = b => {
    setMarkPaidBooking(b);
    setBForm({ transaction_id: b.transaction_id || "" });
  };

  return (
    <div className="fade-up space-y-4">
      {delBooking && (
        <ConfirmModal
          title={t("title_cancel_booking")}
          message={t("msg_cancel_booking_confirm", { name: delBooking.booked_by, date: delBooking.date })}
          onConfirm={confirmDelBooking}
          onCancel={() => !cancelBookingLoading && setDelBooking(null)}
          loading={cancelBookingLoading}
        />
      )}
      {delAmenity && (
        <ConfirmModal
          title={t("title_delete_amenity")}
          message={t("msg_delete_amenity_confirm", { name: delAmenity.name })}
          onConfirm={confirmDelAmenity}
          onCancel={() => !delAmenityLoading && setDelAmenity(null)}
          loading={delAmenityLoading}
        />
      )}

      {markPaidBooking && (
        <Modal title={t("title_mark_paid")} onClose={() => setMarkPaidBooking(null)}>
          <div className="text-sm text-muted mb-4">{t("msg_mark_paid_confirm")}</div>
          <FormRow>
            <Field label={t("label_transaction_id")} value={bForm.transaction_id} onChange={bf("transaction_id")} placeholder={t("placeholder_transaction_id")} />
          </FormRow>
          <FormActions
            onSave={confirmMarkPaid}
            onCancel={() => setMarkPaidBooking(null)}
            label={t("btn_mark_paid")}
            loading={markPaidLoading}
          />
        </Modal>
      )}

      {viewBooking && (
        <Modal title={t("title_view_booking")} onClose={() => setViewBooking(null)}>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("col_booking_id")}</span>
              <span className="font-medium text-ink">{viewBooking.booking_id || "-"}</span>
            </div>
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("col_amenity")}</span>
              <span className="font-medium text-ink">{viewBooking.amenity?.name || "-"}</span>
            </div>
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("label_booked_by")}</span>
              <span className="font-medium text-ink">{viewBooking.booked_by} ({viewBooking.unit})</span>
            </div>
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("col_date")} & {t("col_time_slot")}</span>
              <span className="font-medium text-ink">{viewBooking.date} • {viewBooking.time_slot || "-"}</span>
            </div>
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("col_amount")}</span>
              <span className="font-medium text-ink">₹{parseFloat(viewBooking.amount || 0).toLocaleString()}</span>
            </div>
            {viewBooking.number_of_people && (
              <div className="flex justify-between pb-3 border-b border-border">
                <span className="text-muted font-medium">{t("label_number_of_people")}</span>
                <span className="font-medium text-ink">{viewBooking.number_of_people}</span>
              </div>
            )}
            <div className="flex justify-between pb-3 border-b border-border">
              <span className="text-muted font-medium">{t("col_payment")}</span>
              <span className="font-medium text-ink capitalize">{viewBooking.payment_status}</span>
            </div>
            <div className="flex justify-between pb-3">
              <span className="text-muted font-medium">{t("col_status")}</span>
              <Badge color={viewBooking.booking_status === "approved" ? "green" : viewBooking.booking_status === "cancelled" ? "gray" : viewBooking.booking_status === "completed" ? "blue" : "amber"} dot>
                {viewBooking.booking_status === "approved" ? t("status_approved") : viewBooking.booking_status === "cancelled" ? t("status_cancelled") : viewBooking.booking_status === "completed" ? t("status_completed") : t("status_pending")}
              </Badge>
            </div>
            {viewBooking.payment_status === 'paid' && (
              <div className="flex justify-end pt-3 border-t border-border mt-3">
                <Btn sm variant="outline" onClick={() => handleDownloadReceipt(viewBooking)}>
                  <Icon name="download" size={14} className="mr-1.5" />
                  {t("btn_download_receipt") || "Receipt"}
                </Btn>
              </div>
            )}
            {isAdmin && viewBooking.booking_status === 'pending' && (
              <div className="flex gap-2 justify-end pt-3 border-t border-border mt-4">
                <Btn sm variant="secondary" onClick={() => { handleRejectBooking(viewBooking); setViewBooking(null); }}>
                  Reject Request
                </Btn>
                <Btn sm variant="primary" onClick={() => { handleApproveBooking(viewBooking); setViewBooking(null); }}>
                  Approve Request
                </Btn>
              </div>
            )}
          </div>
        </Modal>
      )}

      {modal === "booking" && (
        <Modal title={editBooking ? t("title_edit_booking") : t("title_new_booking")} onClose={() => { setModal(null); setEditBooking(null); }}>
          <FormRow>
            <Field label={t("label_amenity")} type="select" value={bForm.amenity_id} onChange={(e) => {
              const val = e.target.value;
              const selected = amenities?.find(a => String(a.id) === String(val));
              let amt = bForm.amount;
              if (selected && selected.price) {
                const match = selected.price.match(/\d+/);
                if (match) amt = match[0];
                else if (selected.price.toLowerCase().includes('free')) amt = "0";
              }
              setBForm(f => ({ ...f, amenity_id: val, amount: amt }));
              if (errors.amenity_id) setErrors(prev => ({ ...prev, amenity_id: null }));
            }}
              error={errors.amenity_id}
              options={[
                { value: "", label: t("opt_select_amenity") },
                ...(amenities || []).filter(a => a.is_active || a.id === bForm.amenity_id).map(a => ({ value: a.id, label: a.name }))
              ]} required />
          </FormRow>
          <FormRow cols={3}>
            <Field label={t("label_booked_by")} value={bForm.booked_by} onChange={bf("booked_by")} error={errors.booked_by} required />
            <SearchableSelect
              label="Block"
              value={selectedBlock}
              placeholder="Select Block"
              searchPlaceholder="Search block..."
              onChange={(e) => {
                const blk = e.target.value;
                setSelectedBlock(blk);
                if (blk) {
                  const isCurrentUnitInBlock = (units || []).some(u => String(u.unit_number) === String(bForm.unit) && String(u.block) === String(blk));
                  if (!isCurrentUnitInBlock) {
                    setBForm(prev => ({ ...prev, unit: "" }));
                  }
                }
                if (errors.block) setErrors(prev => ({ ...prev, block: null }));
              }}
              error={errors.block}
              disabled={isMember}
              options={[
                { value: "", label: "Select Block" },
                ...availableBlocks.map(b => ({ value: b, label: `Block ${b}` }))
              ]}
              required
            />
            <SearchableSelect
              label={t("label_unit")}
              value={bForm.unit}
              placeholder="Select Unit No."
              searchPlaceholder="Search unit number..."
              onChange={(e) => {
                const selectedUnitNo = e.target.value;
                const matched = (units || []).find(u => String(u.unit_number) === String(selectedUnitNo) && (!selectedBlock || String(u.block) === String(selectedBlock)))
                  || (units || []).find(u => String(u.unit_number) === String(selectedUnitNo));
                if (matched?.block && !selectedBlock) {
                  setSelectedBlock(matched.block);
                  if (errors.block) setErrors(prev => ({ ...prev, block: null }));
                }
                setBForm(prev => ({
                  ...prev,
                  unit: selectedUnitNo,
                  booked_by: (!prev.booked_by && matched?.owner_name) ? matched.owner_name : prev.booked_by
                }));
                if (errors.unit) setErrors(prev => ({ ...prev, unit: null }));
              }}
              error={errors.unit}
              disabled={isMember}
              options={[
                { value: "", label: "Select Unit No." },
                ...(filteredUnitsForBlock || [])
                  .filter(u => u && u.unit_number !== undefined && u.unit_number !== null && u.unit_number !== "")
                  .map(u => ({
                    value: u.unit_number,
                    label: selectedBlock
                      ? `${u.unit_number}${u.owner_name ? ` (${u.owner_name})` : ''}`
                      : `${u.block ? `${u.block} - ` : ''}${u.unit_number}${u.owner_name ? ` (${u.owner_name})` : ''}`
                  }))
              ]}
              required
            />
          </FormRow>
          <FormRow cols={3}>
            <Field label={t("label_date")} type="date" value={bForm.date} onChange={bf("date")} error={errors.date} required />
            <Field label={t("label_start_time")} type="time" value={bForm.start_time} onChange={bf("start_time")} error={errors.start_time} required />
            <Field label={t("label_end_time")} type="time" value={bForm.end_time} onChange={bf("end_time")} error={errors.end_time} required />
          </FormRow>
          {(() => {
            const selected = amenities?.find(a => String(a.id) === String(bForm.amenity_id));
            return (
              <>
                {selected && ['per_person', 'per_person_per_slot'].includes(selected.price_type) && (
                  <FormRow>
                    <Field label={t("label_number_of_people")} type="number" value={bForm.number_of_people} onChange={bf("number_of_people")} required />
                  </FormRow>
                )}
                <FormRow cols={editBooking ? 3 : 2}>
                  <Field label={t("label_amount")} type="number" value={bForm.amount} onChange={bf("amount")} error={errors.amount} required />
                  <Field label={t("label_payment_status")} type="select" value={isMember ? "pending" : bForm.payment_status} onChange={bf("payment_status")} options={[{ value: "pending", label: t("status_pending") }, { value: "paid", label: t("status_paid") }, { value: "failed", label: t("status_failed") }, { value: "refunded", label: t("status_refunded") }]} disabled={isMember} required />
                  {editBooking && (
                    <Field label={t("label_status")} type="select" value={bForm.booking_status || 'pending'} onChange={bf("booking_status")} options={[{ value: "pending", label: t("status_pending") }, { value: "approved", label: t("status_approved") }, { value: "completed", label: t("status_completed") }, { value: "cancelled", label: t("status_cancelled") }]} required />
                  )}
                </FormRow>
              </>
            );
          })()}
          <FormActions onSave={saveBooking} onCancel={() => { setModal(null); setEditBooking(null); }} label={editBooking ? t("btn_update_booking") : (isMember ? t("btn_request_booking") : t("btn_confirm_booking"))} loading={loading} />
        </Modal>
      )}

      {modal === "amenity" && (
        <Modal title={editAmenity ? t("title_edit_amenity") : t("title_add_amenity")} onClose={() => { setModal(null); setEditAmenity(null); setAForm(defA); }}>
          <FormRow cols={2}>
            <div className="flex flex-col gap-1.5 relative z-50">
              <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{t("label_icon", { defaultValue: "Icon (Emoji)" })}</label>
              <div className="relative group">
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                  value={aForm.icon}
                  onChange={af("icon")}
                  placeholder="🏋️"
                />
                <div className="absolute top-full left-0 mt-1.5 p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl hidden group-hover:grid group-focus-within:grid grid-cols-6 gap-1 w-[220px]">
                  {['🏢', '🏋️', '🏊', '🎾', '🏸', '🎱', '🎮', '🎭', '🎬', '🌳', '🚲', '🧘‍♀️', '🏀', '🏏', '🎤', '📚', '🏃‍♂️'].map(emoji => (
                    <button key={emoji} type="button" onClick={() => af("icon")({ target: { value: emoji } })} className="text-xl p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg cursor-pointer transition-colors flex items-center justify-center">
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("note_emoji", { defaultValue: "Enter an emoji" })} (Hover to pick)</div>
            </div>
            <Field label={t("label_name")} value={aForm.name} onChange={af("name")} error={errors.name} required />
          </FormRow>
          <FormRow cols={3}>
            <Field label={t("label_max_capacity")} type="number" value={aForm.capacity} onChange={af("capacity")} error={errors.capacity} placeholder={t("placeholder_capacity")} required />
            <Field label={t("label_price")} type="number" value={aForm.price} onChange={af("price")} placeholder={t("placeholder_price")} />
            <Field label={t("label_price_type")} type="select" value={aForm.price_type} onChange={af("price_type")} options={[{ value: "per_slot", label: t("opt_per_slot") }, { value: "per_booking", label: t("opt_per_booking") }, { value: "per_person", label: t("opt_per_person") }, { value: "per_person_per_slot", label: t("opt_per_person_per_slot") }]} required />
          </FormRow>
          <FormRow cols={3}>
            <Field label={t("label_opening_time")} type="time" value={aForm.opening_time} onChange={af("opening_time")} required />
            <Field label={t("label_closing_time")} type="time" value={aForm.closing_time} onChange={af("closing_time")} required />
            <Field label={t("label_slot_duration")} type="number" value={aForm.slot_duration_minutes} onChange={af("slot_duration_minutes")} placeholder="e.g. 60" required />
          </FormRow>
          <FormRow cols={2}>
            <Field label="Status" type="select" value={aForm.status} onChange={af("status")} options={[{ value: "Available", label: t("status_available") }, { value: "Booked", label: t("status_booked") }, { value: "Maintenance", label: t("status_maintenance") }, { value: "Closed", label: t("status_closed") }]} required />
            <Field label="Active" type="select" value={String(aForm.is_active)} onChange={e => af("is_active")({ target: { value: e.target.value === 'true' } })} options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]} required />
          </FormRow>
          <FormActions onSave={saveAmenity} onCancel={() => { setModal(null); setEditAmenity(null); setAForm(defA); }} label={editAmenity ? t("btn_update_amenity") : t("btn_add_amenity")} loading={loading} />
        </Modal>
      )}

      {viewAmenity && (
        <Modal title={t("title_view_amenity")} onClose={() => setViewAmenity(null)}>
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">{viewAmenity.icon || '🏢'}</div>
            <div className="text-xl font-bold">{viewAmenity.name}</div>
            <div className="mt-2">
              <Badge color={!viewAmenity.is_active ? "gray" : (STATUS_COLOR_MAP[viewAmenity.status] || "green")} dot>{!viewAmenity.is_active ? "Disabled" : (viewAmenity.status === "Available" ? t("status_available") : viewAmenity.status === "Booked" ? t("status_booked") : viewAmenity.status === "Maintenance" ? t("status_maintenance") : t("status_closed"))}</Badge>
            </div>
          </div>
          <div className="space-y-3 bg-slate-50 dark:bg-slate-800 p-4 rounded-xl text-sm">
            <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2"><span className="text-muted">{t("label_max_capacity")}</span><span className="font-medium">{viewAmenity.capacity}</span></div>
            <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2"><span className="text-muted">{t("label_price")}</span><span className="font-medium">{viewAmenity.price ? `₹${viewAmenity.price}` : t("label_free")}</span></div>
            <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2"><span className="text-muted">{t("label_price_type")}</span><span className="font-medium">{viewAmenity.price_type === 'per_person' ? t("opt_per_person") : viewAmenity.price_type === 'per_person_per_slot' ? t("opt_per_person_per_slot") : viewAmenity.price_type === 'per_booking' ? t("opt_per_booking") : t("opt_per_slot")}</span></div>
            <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2"><span className="text-muted">{t("label_opening_time")}</span><span className="font-medium">{viewAmenity.opening_time ? viewAmenity.opening_time.substring(0, 5) : "-"}</span></div>
            <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2"><span className="text-muted">{t("label_closing_time")}</span><span className="font-medium">{viewAmenity.closing_time ? viewAmenity.closing_time.substring(0, 5) : "-"}</span></div>
            <div className="flex justify-between"><span className="text-muted">{t("label_slot_duration")}</span><span className="font-medium">{viewAmenity.slot_duration_minutes} min</span></div>
          </div>
          <div className="mt-5 pt-4 border-t border-border flex justify-end">
            <Btn onClick={() => setViewAmenity(null)}>{t("btn_close", { defaultValue: "Close" })}</Btn>
          </div>
        </Modal>
      )}

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 h-[88px] animate-pulse" />
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="h-4 w-24 bg-slate-200 dark:bg-slate-600 rounded animate-pulse" />
              <div className="h-8 w-28 bg-slate-200 dark:bg-slate-600 rounded-lg animate-pulse" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 h-44 animate-pulse flex flex-col items-center justify-center">
                  <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-full mb-3" />
                  <div className="w-20 h-4 bg-slate-200 dark:bg-slate-600 rounded mb-2" />
                  <div className="w-16 h-3 bg-slate-200 dark:bg-slate-600 rounded" />
                </div>
              ))}
            </div>
          </div>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="h-5 w-40 bg-slate-200 dark:bg-slate-600 rounded animate-pulse" />
              <div className="h-8 w-32 bg-slate-200 dark:bg-slate-600 rounded-lg animate-pulse" />
            </div>
            <div className="space-y-3">
              <div className="h-10 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse" />
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="h-12 bg-slate-50 dark:bg-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          </Card>
        </div>
      ) : (
        <>
          {!isMember && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <MetricCard label={t("card_total_amenities")} value={Number(metrics?.total_amenities ?? 0).toLocaleString('en-IN')} icon="building" accent="blue" onClick={() => setActiveFilter('all')} className={activeFilter === 'all' ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900' : ''} />
              <MetricCard label={t("card_bookings_month")} value={Number(metrics?.bookings_this_month ?? 0).toLocaleString('en-IN')} icon="trend_up" accent="green" onClick={() => setActiveFilter('this_month')} className={activeFilter === 'this_month' ? 'ring-2 ring-green-500 ring-offset-2 dark:ring-offset-slate-900' : ''} />
              <MetricCard
                label={t("card_revenue_month", { defaultValue: "Revenue This Month" })}
                value={`₹${Number(metrics?.total_booked_amount_this_month || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                sublabel={`Paid: ₹${Number(metrics?.revenue_this_month || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                icon="money"
                accent="amber"
                onClick={() => setActiveFilter('revenue')}
                className={activeFilter === 'revenue' ? 'ring-2 ring-amber-500 ring-offset-2 dark:ring-offset-slate-900' : ''}
              />
              <MetricCard label={t("card_pending_payments")} value={Number(metrics?.pending_payments ?? 0).toLocaleString('en-IN')} icon="alert" accent="red" onClick={() => setActiveFilter('pending')} className={activeFilter === 'pending' ? 'ring-2 ring-red-500 ring-offset-2 dark:ring-offset-slate-900' : ''} />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-ink">{t("title_amenities")}</div>
              {!isMember && <Btn sm variant="primary" icon="plus" onClick={() => { setEditAmenity(null); setAForm(defA); setErrors({}); setModal("amenity"); }}>{t("btn_add_amenity")}</Btn>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {amenities?.map(a => (
                <Card
                  key={a.id}
                  hover
                  noPad
                  onClick={() => setViewAmenity(a)}
                  className={`text-center p-3.5 sm:p-4 flex flex-col justify-between relative group cursor-pointer transition-all ${!a.is_active ? 'opacity-60 grayscale' : ''}`}
                >
                  <div className="flex-1 flex flex-col items-center">
                    <div className="text-3xl mb-2 select-none transform group-hover:scale-105 transition-transform duration-200">
                      {a.icon || '🏢'}
                    </div>
                    <div className="text-sm font-bold text-ink mb-1.5 truncate max-w-full px-1" title={a.name}>
                      {a.name}
                    </div>

                    <div className="flex flex-col items-center gap-1 mb-2.5">
                      <div className="flex items-center gap-1.5 text-xs text-muted">
                        <Icon name="users" size={12} />
                        <span>{t("label_max")} {a.capacity}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted">
                        <span className="font-semibold text-[11px]">₹</span>
                        <span>{a.price || t("label_free")}</span>
                      </div>
                      {a.timing && (
                        <div className="flex items-center gap-1.5 text-xs text-muted">
                          <Icon name="clock" size={12} />
                          <span>{a.timing}</span>
                        </div>
                      )}
                    </div>

                    <div className="mb-2.5">
                      <Badge color={!a.is_active ? "gray" : (STATUS_COLOR_MAP[a.status] || "green")} dot>
                        {!a.is_active ? "Disabled" : (a.status === "Available" ? t("status_available") : a.status === "Booked" ? t("status_booked") : a.status === "Maintenance" ? t("status_maintenance") : t("status_closed"))}
                      </Badge>
                    </div>
                  </div>

                  {!isMember && (
                    <div
                      className="pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-center gap-1 sm:gap-1.5 w-full mt-auto"
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setViewAmenity(a); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-500 dark:hover:text-white transition-colors cursor-pointer"
                        title={t("title_view_amenity")}
                      >
                        <Icon name="eye" size={13} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); handleToggleActive(a); }}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 transition-colors cursor-pointer ${
                          a.is_active
                            ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-500 hover:text-white dark:hover:bg-amber-500 dark:hover:text-white'
                            : 'text-green-600 dark:text-green-400 hover:bg-green-500 hover:text-white dark:hover:bg-green-500 dark:hover:text-white'
                        }`}
                        title={a.is_active ? "Disable" : "Enable"}
                      >
                        <Icon name={a.is_active ? "ban" : "check"} size={13} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); openEditAmenity(a); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-colors cursor-pointer"
                        title={t("title_edit_amenity_tooltip")}
                      >
                        <Icon name="edit" size={13} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setDelAmenity(a); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-red-500 dark:text-red-400 hover:bg-red-500 hover:text-white dark:hover:bg-red-500 dark:hover:text-white transition-colors cursor-pointer"
                        title={t("title_delete_amenity_tooltip")}
                      >
                        <Icon name="trash" size={13} color="currentColor" />
                      </button>
                    </div>
                  )}
                </Card>
              ))}
              {amenities?.length === 0 && (
                <div className="col-span-full py-8 text-center text-muted text-sm bg-slate-50/50 dark:bg-slate-800/50 rounded-xl border border-dashed border-border">
                  {t("msg_no_amenities")}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 mb-4 border-b border-border mt-8">
            <button onClick={() => setActiveTab('table')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'table' ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`}>List View</button>
            <button onClick={() => setActiveTab('calendar')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'calendar' ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`}>Calendar</button>
            {!isMember && <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'reports' ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`}>Reports</button>}
          </div>

          {activeTab === 'table' && (
            <Card>
              <SectionHeader title={t("title_upcoming_bookings")} action={<Btn sm variant="primary" icon="plus" onClick={openNewBooking}>{isMember ? t("btn_request_booking") : t("btn_new_booking")}</Btn>} />

              <div className="mb-4 bg-slate-50/80 dark:bg-slate-800/40 p-2 sm:p-2.5 rounded-xl border border-slate-200/70 dark:border-slate-700/60">
                {/* Mobile View: Compact Search Bar + Filter Toggle Button */}
                <div className="flex sm:hidden items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Icon name="search" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search bookings..."
                      className="w-full pl-8 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted transition-all"
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(""); setPage(1); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-xs p-1 cursor-pointer"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className={`h-9 px-2.5 flex items-center gap-1.5 rounded-lg border text-xs font-semibold shrink-0 transition-all cursor-pointer ${
                      showMobileFilters || activeFiltersCount > 0
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Icon name="filter" size={13} color="currentColor" />
                    <span>{t("btn_filters", { defaultValue: "Filters" })}</span>
                    {activeFiltersCount > 0 && (
                      <span className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${showMobileFilters || activeFiltersCount > 0 ? 'bg-white text-primary' : 'bg-primary text-white'}`}>
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Mobile Collapsible Filters: 2-column compact grid */}
                {showMobileFilters && (
                  <div className="sm:hidden grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                    <div className="relative">
                      <select
                        className="w-full pl-2.5 pr-7 py-1.5 h-8.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none truncate"
                        value={filterAmenity}
                        onChange={e => { setFilterAmenity(e.target.value); setPage(1); }}
                      >
                        <option value="">All Amenities</option>
                        {amenities?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      <Icon name="chevron_down" size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>

                    <div className="relative">
                      <select
                        className="w-full pl-2.5 pr-7 py-1.5 h-8.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none truncate"
                        value={filterPayment}
                        onChange={e => { setFilterPayment(e.target.value); setPage(1); }}
                      >
                        <option value="">All Payments</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                      <Icon name="chevron_down" size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>

                    <div className="relative">
                      <select
                        className="w-full pl-2.5 pr-7 py-1.5 h-8.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none truncate"
                        value={filterBooking}
                        onChange={e => { setFilterBooking(e.target.value); setPage(1); }}
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <Icon name="chevron_down" size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>

                    <div className="relative">
                      <select
                        className="w-full pl-2.5 pr-7 py-1.5 h-8.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none truncate"
                        value={`${sortField}-${sortOrder}`}
                        onChange={e => {
                          const [f, o] = e.target.value.split('-');
                          setSortField(f);
                          setSortOrder(o);
                          setPage(1);
                        }}
                      >
                        <option value="date-desc">Newest First</option>
                        <option value="date-asc">Oldest First</option>
                        <option value="amount-desc">Highest Amount</option>
                        <option value="amount-asc">Lowest Amount</option>
                      </select>
                      <Icon name="chevron_down" size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Desktop Unified Toolbar (sm and up) */}
                <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
                  <div className="relative">
                    <Icon name="search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search bookings..."
                      className="w-full pl-8 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow placeholder:text-muted"
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(""); setPage(1); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-xs p-1 cursor-pointer"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="relative">
                    <select
                      className="w-full pl-3 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow appearance-none truncate"
                      value={filterAmenity}
                      onChange={e => { setFilterAmenity(e.target.value); setPage(1); }}
                    >
                      <option value="">All Amenities</option>
                      {amenities?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <Icon name="chevron_down" size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  </div>

                  <div className="relative">
                    <select
                      className="w-full pl-3 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow appearance-none truncate"
                      value={filterPayment}
                      onChange={e => { setFilterPayment(e.target.value); setPage(1); }}
                    >
                      <option value="">All Payments</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                    <Icon name="chevron_down" size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  </div>

                  <div className="relative">
                    <select
                      className="w-full pl-3 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow appearance-none truncate"
                      value={filterBooking}
                      onChange={e => { setFilterBooking(e.target.value); setPage(1); }}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <Icon name="chevron_down" size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  </div>

                  <div className="relative">
                    <select
                      className="w-full pl-3 pr-7 py-1.5 h-9 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow appearance-none truncate"
                      value={`${sortField}-${sortOrder}`}
                      onChange={e => {
                        const [f, o] = e.target.value.split('-');
                        setSortField(f);
                        setSortOrder(o);
                        setPage(1);
                      }}
                    >
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="amount-desc">Highest Amount</option>
                      <option value="amount-asc">Lowest Amount</option>
                    </select>
                    <Icon name="chevron_down" size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  </div>
                </div>

                {/* Active Filter Chips */}
                {hasActiveFilters && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                    <span className="text-[11px] text-muted font-medium mr-0.5">Active:</span>
                    {searchQuery && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[11px]">
                        <span>"{searchQuery}"</span>
                        <button type="button" onClick={() => { setSearchQuery(""); setPage(1); }} className="hover:text-red-500 ml-0.5 cursor-pointer">✕</button>
                      </span>
                    )}
                    {filterAmenity && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 border border-primary/20 text-primary text-[11px] font-medium">
                        <span>{amenities?.find(a => String(a.id) === String(filterAmenity))?.name || "Amenity"}</span>
                        <button type="button" onClick={() => { setFilterAmenity(""); setPage(1); }} className="hover:text-red-500 ml-0.5 cursor-pointer">✕</button>
                      </span>
                    )}
                    {filterPayment && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-[11px] font-medium capitalize">
                        <span>Payment: {filterPayment}</span>
                        <button type="button" onClick={() => { setFilterPayment(""); setPage(1); }} className="hover:text-red-500 ml-0.5 cursor-pointer">✕</button>
                      </span>
                    )}
                    {filterBooking && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-700 dark:text-blue-400 text-[11px] font-medium capitalize">
                        <span>Status: {filterBooking}</span>
                        <button type="button" onClick={() => { setFilterBooking(""); setPage(1); }} className="hover:text-red-500 ml-0.5 cursor-pointer">✕</button>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={resetAllFilters}
                      className="text-[11px] text-muted hover:text-red-500 font-medium underline ml-1 cursor-pointer"
                    >
                      {t("btn_clear_filters") || "Clear all"}
                    </button>
                  </div>
                )}
              </div>

              <DataTable cols={[t("col_booking_id"), t("col_amenity"), t("col_booked_by"), t("col_unit"), t("col_date"), t("col_time_slot"), t("col_amount"), t("col_payment"), t("col_status"), t("col_actions")]}
                rows={paginatedBookings.map(b => [
                  <span className="font-mono text-xs">{b.booking_id || "-"}</span>,
                  b.amenity?.name || "-",
                  <div className="flex items-center gap-1.5"><Avatar name={b.booked_by} size={22} /><span className="text-xs font-medium">{b.booked_by}</span></div>,
                  <span className="font-mono text-xs bg-slate-50 dark:bg-slate-800 px-1.5 py-px rounded">{b.unit}</span>,
                  b.date, b.time_slot || "-", `₹${parseFloat(b.amount || 0).toLocaleString()}`,
                  <Badge color={b.payment_status === "paid" ? "green" : b.payment_status === "failed" ? "red" : b.payment_status === "refunded" ? "gray" : "amber"} dot>{b.payment_status === "paid" ? t("status_paid") : b.payment_status === "failed" ? t("status_failed") : b.payment_status === "refunded" ? t("status_refunded") : t("status_pending")}</Badge>,
                  <Badge color={b.booking_status === "approved" ? "green" : b.booking_status === "cancelled" ? "gray" : b.booking_status === "completed" ? "blue" : "amber"} dot>{b.booking_status === "approved" ? t("status_approved") : b.booking_status === "cancelled" ? t("status_cancelled") : b.booking_status === "completed" ? t("status_completed") : t("status_pending")}</Badge>,
                  <div className="flex gap-1 items-center">
                    <button onClick={() => setViewBooking(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-blue-500 transition-colors" title={t("title_view_booking")}>
                      <Icon name="eye" size={14} />
                    </button>
                    {isAdmin && b.booking_status === 'pending' && (
                      <button onClick={() => handleApproveBooking(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 transition-colors" title="Approve Request">
                        <Icon name="check" size={14} />
                      </button>
                    )}
                    {isAdmin && b.booking_status === 'pending' && (
                      <button onClick={() => handleRejectBooking(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-600 dark:text-rose-400 transition-colors" title="Reject Request">
                        <Icon name="x" size={14} />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => openEditBooking(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-amber-500 transition-colors" title="Edit Booking">
                        <Icon name="edit" size={14} />
                      </button>
                    )}
                    {isAdmin && b.payment_status === 'pending' && b.booking_status !== 'cancelled' && (
                      <button onClick={() => openMarkPaid(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-green-500 transition-colors" title={t("btn_mark_paid")}>
                        <Icon name="money" size={14} />
                      </button>
                    )}
                    {b.payment_status === 'paid' && (
                      <button onClick={() => handleDownloadReceipt(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-primary transition-colors" title={t("btn_download_receipt")}>
                        <Icon name="download" size={14} />
                      </button>
                    )}
                    {(!isMember || b.unit === user?.unit?.unit_number) && b.booking_status !== 'cancelled' && b.booking_status !== 'pending' && (
                      <button onClick={() => setDelBooking(b)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-red-500 transition-colors" title={t("title_cancel_booking")}>
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                ])}
                emptyMsg={t("msg_no_bookings")}
                footer={totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted">Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, filteredBookings.length)} of {filteredBookings.length}</div>
                    <div className="flex gap-1">
                      <Btn sm variant="secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Btn>
                      <Btn sm variant="secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Btn>
                    </div>
                  </div>
                )}
              />
            </Card>
          )}

          {activeTab === 'calendar' && (
            <CalendarView
              amenities={amenities}
              bookings={bookings}
              onBookSlot={(amenity_id, date, start_time, end_time) => {
                setBForm(f => ({ ...f, amenity_id, date, start_time, end_time }));
                setModal("booking");
              }}
            />
          )}

          {activeTab === 'reports' && !isMember && (
            <ReportsView bookings={bookings} amenities={amenities} />
          )}
        </>
      )}
    </div>
  );
}
